import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, MenuItem, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { listDeveloperManagers, createAccount, listManageableClusters, type AccountSummary } from '../../lib/accounts-api';
import { extractApiError } from '../../lib/api-client';
import { CredentialsDialog } from '../platform/CredentialsDialog';
import { AccountRowActions, CreateAccountDialogShell } from './AccountDialogs';
import { useT } from '../../i18n';

export function DeveloperManagersPage() {
  const t = useT();
  const [items, setItems] = useState<AccountSummary[]>([]);
  const [devOptions, setDevOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [credits, setCredits] = useState<{ email: string; password: string } | null>(null);

  async function refresh() {
    setLoading(true); setError(null);
    try {
      const [accts, devs] = await Promise.all([listDeveloperManagers(), listManageableClusters()]);
      setItems(accts);
      setDevOptions(devs.filter((d) => d.clusters.length > 0).map((d) => ({ id: d.id, name: d.name })));
    } catch (e) { setError(extractApiError(e).message); } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box sx={{ maxWidth: 640 }}>
          <Typography variant="h4" fontWeight={600}>{t('devmgr.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('devmgr.subtitle')}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={devOptions.length === 0} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          {t('devmgr.newBtn')}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}><Typography color="text.secondary">{t('devmgr.empty')}</Typography></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead><TableRow>
                  <TableCell>{t('common.colName')}</TableCell><TableCell>{t('common.email')}</TableCell><TableCell>{t('devmgr.colDeveloper')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell><TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {items.map((a) => (
                    <TableRow key={a.id} hover>
                      <TableCell>{a.full_name}</TableCell>
                      <TableCell>{a.email}</TableCell>
                      <TableCell>{a.developer_name ?? '—'}</TableCell>
                      <TableCell>{a.status}</TableCell>
                      <TableCell align="right"><AccountRowActions account={a} onChanged={refresh} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <CreateDeveloperManagerDialog
        open={creating}
        developers={devOptions}
        onClose={() => setCreating(false)}
        onCreated={(email, password) => { setCreating(false); setCredits({ email, password }); refresh(); }}
      />
      <CredentialsDialog credits={credits} onClose={() => setCredits(null)} />
    </Stack>
  );
}

function CreateDeveloperManagerDialog({ open, developers, onClose, onCreated }: {
  open: boolean; developers: { id: string; name: string }[]; onClose: () => void; onCreated: (email: string, password: string) => void;
}) {
  const t = useT();
  const [developerId, setDeveloperId] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setDeveloperId(developers[0]?.id ?? ''); setFullName(''); setEmail(''); setError(null); } }, [open, developers]);
  const canSave = useMemo(() => Boolean(developerId && fullName.trim() && email.trim()), [developerId, fullName, email]);
  async function save() {
    setBusy(true); setError(null);
    try {
      const res = await createAccount({ type: 'developer_manager', target_id: developerId, full_name: fullName.trim(), email: email.trim() });
      onCreated(res.user.email, res.temp_password);
    } catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
  }
  return (
    <CreateAccountDialogShell title={t('devmgr.dialogTitle')} open={open} onClose={onClose} busy={busy} canSave={canSave} onSave={save} error={error}>
      <TextField select label={t('devmgr.colDeveloper')} value={developerId} onChange={(e) => setDeveloperId(e.target.value)} required fullWidth helperText={t('devmgr.devHelper')}>
        {developers.map((d) => (<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>))}
      </TextField>
      <TextField label={t('common.fullName')} value={fullName} onChange={(e) => setFullName(e.target.value)} required fullWidth />
      <TextField label={t('common.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
    </CreateAccountDialogShell>
  );
}
